import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, ClipboardList, Boxes, FileText, TrendingUp, TrendingDown, UserCheck, BarChart3, Flame, CalendarClock, Wallet, Landmark, BookOpen, SlidersHorizontal, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { API, inr } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { GlossyBadge } from "@/components/GlossyBadge";
import { EmptyState } from "@/components/EmptyState";

function StockBarCard({ title, data, dataKey, color, testid, isMoney }) {
  return (
    <Card className="border-slate-200">
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        <div className="h-64" data-testid={testid}>
          {data.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={56} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => (isMoney ? inr(v) : v)} />
                <Bar dataKey={dataKey} fill={color} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center"><EmptyState compact icon={BarChart3} title="No data yet" /></div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StockAnalytics() {
  const { user } = useAuth();
  const canSeeValue = ["admin", "staff", "stock_head"].includes(user?.role);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [stock, setStock] = useState([]);
  const [whName, setWhName] = useState("Office Stock");
  const [inData, setInData] = useState([]);
  const [prodQuery, setProdQuery] = useState("");
  const [prodSel, setProdSel] = useState(null);
  const [prodOpen, setProdOpen] = useState(false);
  const [period, setPeriod] = useState("daily");
  const [stockWh, setStockWh] = useState("all");

  useEffect(() => {
    API.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    API.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    API.get("/inventory/stock").then((r) => setStock(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!whName) return;
    API.get("/inventory/analytics/inbound-trend", { params: { warehouse: whName, product_id: prodSel?.id || "", period } })
      .then((r) => setInData(r.data))
      .catch(() => {});
  }, [whName, prodSel, period]);

  const qtyOf = (p) => (stockWh === "all" ? p.total_stock || 0 : p.stock_by_warehouse?.[stockWh] ?? 0);
  const valOf = (p) => (p.cost_price === null || p.cost_price === undefined ? null : qtyOf(p) * p.cost_price);
  const byQtyDesc = [...stock].sort((a, b) => qtyOf(b) - qtyOf(a));
  const topQty = byQtyDesc.slice(0, 7).map((p) => ({ name: p.name, qty: qtyOf(p) }));
  const lowQty = byQtyDesc.slice(-7).reverse().map((p) => ({ name: p.name, qty: qtyOf(p) }));
  const withVal = stock.map((p) => ({ ...p, _v: valOf(p) })).filter((p) => p._v !== null);
  const byValDesc = [...withVal].sort((a, b) => b._v - a._v);
  const topVal = byValDesc.slice(0, 7).map((p) => ({ name: p.name, value: p._v }));
  const lowVal = byValDesc.slice(-7).reverse().map((p) => ({ name: p.name, value: p._v }));

  return (
    <div className="space-y-6">
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><BarChart3 className="h-4 w-4 text-indigo-500" /> Inbound to Warehouse Over Time</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Select value={whName} onValueChange={setWhName}>
              <SelectTrigger data-testid="wh-graph-select"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
              <SelectContent>
                {warehouses.map((w) => <SelectItem key={w.id} value={w.name}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative">
              <Input placeholder="All products — search to filter..." value={prodQuery} onFocus={() => setProdOpen(true)} onBlur={() => setTimeout(() => setProdOpen(false), 200)} onChange={(e) => { setProdQuery(e.target.value); setProdOpen(true); }} data-testid="trend-product-search" />
              {prodOpen && (
                <div className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-md" data-testid="trend-product-list">
                  <button className="block w-full px-3 py-2 text-left text-sm font-medium text-indigo-700 hover:bg-slate-100" data-testid="trend-product-option-all" onClick={() => { setProdSel(null); setProdQuery(""); setProdOpen(false); }}>
                    All products
                  </button>
                  {products.filter((p) => p.name.toLowerCase().includes(prodQuery.toLowerCase())).map((p) => (
                    <button key={p.id} className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-100" data-testid={`trend-product-option-${p.id}`} onClick={() => { setProdSel(p); setProdQuery(p.name); setProdOpen(false); }}>
                      {p.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="trend-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily (30 days)</SelectItem>
                <SelectItem value="weekly">Weekly (12 weeks)</SelectItem>
                <SelectItem value="monthly">Monthly (12 months)</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="h-72" data-testid="wh-graph">
            {inData.some((d) => d.qty > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={inData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={period === "daily" ? 4 : 0} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="qty" fill="#4F46E5" radius={[4, 4, 0, 0]} name={prodSel ? `Inbound · ${prodSel.name}` : "Inbound qty"} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center"><EmptyState compact icon={TrendingUp} title="No inbound movements in this period" /></div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-slate-600">Warehouse filter:</span>
        <Select value={stockWh} onValueChange={setStockWh}>
          <SelectTrigger className="w-64" data-testid="stock-graph-warehouse-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Warehouses (combined)</SelectItem>
            {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <StockBarCard title="Top 7 — Highest Stock (Qty)" data={topQty} dataKey="qty" color="#4F46E5" testid="stock-high-graph" />
        <StockBarCard title="Top 7 — Lowest Stock (Qty)" data={lowQty} dataKey="qty" color="#F59E0B" testid="stock-low-graph" />
        {canSeeValue && <StockBarCard title="Top 7 — Highest Stock Value" data={topVal} dataKey="value" color="#10B981" testid="value-high-graph" isMoney />}
        {canSeeValue && <StockBarCard title="Top 7 — Lowest Stock Value" data={lowVal} dataKey="value" color="#F43F5E" testid="value-low-graph" isMoney />}
      </div>
    </div>
  );
}

const STAGE_LABELS = { new: "New", contacted: "Contacted", qualified: "Qualified", proposal: "Proposal", won: "Won", lost: "Lost" };

export default function Dashboard() {
  const { user } = useAuth();
  const isStockRole = ["stock_team", "stock_head"].includes(user?.role);
  const [digest, setDigest] = useState(null);
  useEffect(() => {
    if (user?.role === "admin") API.get("/digest/latest").then((r) => setDigest(r.data)).catch(() => {});
  }, [user?.role]);
  const showStockAnalytics = ["admin", "staff", "sales"].includes(user?.role);
  const [data, setData] = useState(null);
  const [sales, setSales] = useState(null);
  const [salePeriod, setSalePeriod] = useState("30d");
  const [widgets, setWidgets] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dash_widgets") || "{}"); } catch { return {}; }
  });
  const showWidget = (k) => widgets[k] !== false;
  const toggleWidget = (k) => setWidgets((w) => {
    const n = { ...w, [k]: w[k] === false };
    localStorage.setItem("dash_widgets", JSON.stringify(n));
    return n;
  });

  useEffect(() => {
    API.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (["admin", "accountant"].includes(user?.role)) API.get("/dashboard/sales-series", { params: { period: salePeriod } }).then((r) => setSales(r.data)).catch(() => {});
  }, [salePeriod, user?.role]);

  if (!data) return <div className="text-sm text-slate-500" data-testid="dashboard-loading">Loading dashboard...</div>;

  if (isStockRole) {
    return (
      <div className="space-y-8" data-testid="stock-dashboard">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Stock Dashboard</h1>
          <p className="text-sm text-slate-500">Warehouse movement analytics</p>
        </div>
        <StockAnalytics />
      </div>
    );
  }

  const stageLabels = Object.fromEntries((data.stages || []).map((s) => [s.id, s.label]));
  const chartData = Object.entries(data.leads_by_stage).map(([k, v]) => ({
    stage: stageLabels[k] || STAGE_LABELS[k] || k,
    leads: v.count,
    value: v.value,
  }));

  const stats = [
    { label: "Pipeline Value", value: inr(data.pipeline_value), icon: TrendingUp, tone: "blue", testid: "stat-pipeline-value" },
    { label: "Open Tasks", value: data.tasks_open, icon: ClipboardList, tone: "indigo", testid: "stat-open-tasks" },
    { label: "Stock Value", value: data.stock_value === null ? "—" : inr(data.stock_value), icon: Boxes, tone: "sky", testid: "stat-stock-value" },
    { label: "Customers", value: data.customers, icon: UserCheck, tone: "navy", testid: "stat-customers" },
    { label: "Quotations", value: data.quotations, icon: FileText, tone: "blue", testid: "stat-quotations" },
    { label: "Invoices", value: data.invoices, icon: FileText, tone: "indigo", testid: "stat-invoices" },
  ];

  const WIDGETS = [
    ["acct", "Accounting KPIs"], ["kpis", "Core KPI Cards"], ["salesChart", "Total Sale Chart"], ["reports", "Most Used Reports"],
    ["charts", "Leads & Tasks"], ["followups", "Follow-ups"], ["hp", "High Priority Leads"], ["stock", "Stock Analytics"],
  ];
  const REPORT_SHORTCUTS = [
    { label: "Sale Report", to: "/accounts/sales/invoices", testid: "report-sale" },
    { label: "All Transactions", to: "/accounts/sales/payment-in", testid: "report-transactions" },
    { label: "Daybook Report", to: "/accounts/accounts/cash", testid: "report-daybook" },
    { label: "Party Statement", to: "/accounts/accounts/parties", testid: "report-party" },
  ];
  const acct = data.accounting;
  const istHour = new Date(Date.now() + 5.5 * 3600e3).getUTCHours();
  const greeting = istHour < 12 ? "Good morning" : istHour < 17 ? "Good afternoon" : "Good evening";
  const firstName = (user?.name || "").split(" ")[0] || "there";
  const dateStr = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", timeZone: "Asia/Kolkata" });

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500">Your business at a glance</p>
        </div>
        <details className="relative" data-testid="dashboard-customize">
          <summary className="flex cursor-pointer list-none items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50" data-testid="customize-widgets-button">
            <SlidersHorizontal className="h-3.5 w-3.5" /> Customize
          </summary>
          <div className="absolute right-0 z-20 mt-1 w-56 rounded-md border border-slate-200 bg-white p-3 shadow-lg">
            {WIDGETS.map(([k, label]) => (
              <label key={k} className="flex cursor-pointer items-center gap-2 py-1 text-sm text-slate-700" data-testid={`widget-toggle-${k}`}>
                <input type="checkbox" checked={showWidget(k)} onChange={() => toggleWidget(k)} className="accent-indigo-600" /> {label}
              </label>
            ))}
          </div>
        </details>
      </div>

      <Card className="overflow-hidden border-0 bg-gradient-to-r from-[#172554] via-[#1e3a8a] to-[#1d4ed8]" data-testid="dashboard-welcome-banner">
        <CardContent className="flex items-center justify-between gap-4 p-5">
          <div>
            <div className="font-heading text-xl font-bold text-white" data-testid="dashboard-greeting">{greeting}, {firstName}</div>
            <div className="mt-1 text-sm text-blue-200/85">{dateStr} — here&apos;s your business at a glance.</div>
          </div>
          <img src="/welcome-trade.png" alt="" className="hidden h-24 w-auto sm:block" data-testid="dashboard-welcome-art" />
        </CardContent>
      </Card>

      {user?.role === "admin" && digest && (
        <Card className="border-blue-200 bg-blue-50/40" data-testid="dashboard-digest-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-blue-700">
              <CalendarClock className="h-4 w-4" /> Weekly digest — week of {digest.week_of}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-700" data-testid="dashboard-digest-body">
              <span>Cards issued: <b>{digest.cards_total}</b> ({digest.cards?.yellow || 0}Y / {digest.cards?.red || 0}R / {digest.cards?.green || 0}G)</span>
              <span>Late task completions: <b>{digest.late_tasks}</b></span>
              <span>Attendance: <b>{digest.attendance?.late || 0}</b> late clock-ins, <b>{digest.attendance?.absent || 0}</b> absent days</span>
            </div>
            {digest.per_employee?.some((e) => e.absent > 0) && (
              <div className="mt-1.5 text-xs text-slate-500">
                Absences: {digest.per_employee.filter((e) => e.absent > 0).map((e) => `${e.name} (${e.absent})`).join(", ")}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {acct && showWidget("acct") && (
        <div className="grid grid-cols-2 gap-4 xl:grid-cols-4" data-testid="acct-kpi-row">
          <Card className="group border-emerald-200 bg-emerald-50/40 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg" data-testid="kpi-receivable">
            <CardContent className="flex items-center gap-3 p-4">
              <GlossyBadge icon={TrendingUp} size={42} tone="sky" testid="kpi-receivable-badge" />
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-emerald-700">Total Receivable</div>
                <div className="mt-0.5 font-heading text-xl font-bold text-emerald-800">{inr(acct.receivable)}</div>
                <div className="mt-0.5 text-[11px] text-emerald-600">From {acct.receivable_parties} {acct.receivable_parties === 1 ? "party" : "parties"}</div>
              </div>
            </CardContent>
          </Card>
          <Card className="group border-rose-200 bg-rose-50/40 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg" data-testid="kpi-payable">
            <CardContent className="flex items-center gap-3 p-4">
              <GlossyBadge icon={TrendingDown} size={42} tone="blue" testid="kpi-payable-badge" />
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-rose-700">Total Payable</div>
                <div className="mt-0.5 font-heading text-xl font-bold text-rose-700">{inr(acct.payable)}</div>
                <div className="mt-0.5 text-[11px] text-rose-500">To {acct.payable_parties} {acct.payable_parties === 1 ? "party" : "parties"}</div>
              </div>
            </CardContent>
          </Card>
          <Card className="group border-slate-200 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg" data-testid="kpi-cash">
            <CardContent className="flex items-center gap-3 p-4">
              <GlossyBadge icon={Wallet} size={42} tone="indigo" testid="kpi-cash-badge" />
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Cash In Hand</div>
                <div className="mt-0.5 font-heading text-xl font-bold text-slate-900">{inr(acct.cash)}</div>
              </div>
            </CardContent>
          </Card>
          <Card className="group border-slate-200 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg" data-testid="kpi-bank">
            <CardContent className="flex items-center gap-3 p-4">
              <GlossyBadge icon={Landmark} size={42} tone="navy" testid="kpi-bank-badge" />
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Bank Balance</div>
                <div className="mt-0.5 font-heading text-xl font-bold text-slate-900">{inr(acct.bank)}</div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showWidget("kpis") && (
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        {stats.map((s) => (
          <Card key={s.label} className="group border-slate-200 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg" data-testid={s.testid}>
            <CardContent className="p-4">
              <GlossyBadge icon={s.icon} size={34} tone={s.tone} testid={`${s.testid}-badge`} />
              <div className="mt-2.5 text-xs font-medium uppercase tracking-wide text-slate-500">{s.label}</div>
              <div className="mt-1 font-heading text-xl font-bold text-slate-900">{s.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}

      {acct && showWidget("salesChart") && sales && (
        <Card className="border-slate-200" data-testid="total-sale-chart">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-base"><BarChart3 className="h-4 w-4 text-indigo-500" /> Total Sale <span className="font-heading text-lg font-bold text-slate-900">{inr(sales.current_total)}</span>
                <span className={`flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${sales.pct_change >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-600"}`} data-testid="sales-pct-change">
                  {sales.pct_change >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{Math.abs(sales.pct_change)}% vs last period
                </span>
              </CardTitle>
              <Select value={salePeriod} onValueChange={setSalePeriod}>
                <SelectTrigger className="h-8 w-36" data-testid="sales-period-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={sales.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Line type="monotone" dataKey="total" stroke="#4F46E5" strokeWidth={2} dot={false} name="Sales" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {acct && showWidget("reports") && (
        <Card className="border-slate-200" data-testid="reports-shortcut-panel">
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><BookOpen className="h-4 w-4 text-indigo-500" /> Most Used Reports</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {REPORT_SHORTCUTS.map((r) => (
              <Link key={r.testid} to={r.to} className="rounded-md border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700" data-testid={r.testid}>{r.label}</Link>
            ))}
          </CardContent>
        </Card>
      )}

      {showWidget("charts") && (<div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Card className="border-slate-200 xl:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4 text-indigo-500" /> Leads by Stage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64" data-testid="leads-stage-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="stage" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v, name) => (name === "Value (₹)" ? inr(v) : v)} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="leads" fill="#4F46E5" radius={[6, 6, 0, 0]} name="Leads" />
                  <Bar yAxisId="right" dataKey="value" fill="#10B981" radius={[6, 6, 0, 0]} name="Value (₹)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base">Open Tasks</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3" data-testid="recent-tasks-list">
            {data.recent_tasks.length === 0 && <EmptyState compact icon={ClipboardList} title="No open tasks" hint="New tasks you create will show up here." testid="dashboard-tasks-empty" />}
            {data.recent_tasks.map((t) => (
              <div key={t.id} className="rounded-md border border-slate-200 p-3">
                <div className="text-sm font-medium text-slate-900">{t.title}</div>
                <div className="text-xs text-slate-500">
                  {t.assigned_to_name || "Unassigned"} · due {t.due_date || "-"}
                </div>
              </div>
            ))}
            <Link to="/tasks" className="block text-sm font-medium text-indigo-600 hover:text-indigo-800">
              View all tasks →
            </Link>
          </CardContent>
        </Card>
      </div>
      )}

      {showWidget("followups") && (
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="h-4 w-4 text-amber-500" /> Today&apos;s Follow-ups &amp; Overdue
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2" data-testid="followups-list">
          {(!data.followups || data.followups.length === 0) && (
            <EmptyState compact icon={CalendarClock} title="No follow-ups due today" hint="All caught up!" testid="dashboard-followups-empty" />
          )}
          {(data.followups || []).map((f, i) => (
            <div key={i} className={`flex items-center justify-between rounded-md border p-3 ${f.overdue ? "border-rose-200 bg-rose-50/50" : "border-amber-200 bg-amber-50/40"}`} data-testid={`followup-${i}`}>
              <div>
                <div className="text-sm font-medium text-slate-900">{f.title}</div>
                <div className="text-xs text-slate-500">{f.lead_name}{f.phone ? ` · ${f.phone}` : ""}</div>
              </div>
              <div className="text-right">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${f.overdue ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{f.overdue ? "Overdue" : "Today"}</span>
                <div className="mt-0.5 text-xs capitalize text-slate-500">{f.type} · {f.due_date}{f.due_time ? ` ${f.due_time}` : ""}</div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      )}

      {showWidget("hp") && (
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Flame className="h-4 w-4 text-rose-500" /> High Priority Leads</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3" data-testid="high-priority-leads-list">
          {data.high_priority_leads.length === 0 && <EmptyState compact icon={Flame} title="No high-priority leads" hint="Mark a lead as High priority from its detail panel." testid="dashboard-hp-empty" />}
          {data.high_priority_leads.map((l) => (
            <div key={l.id} className="flex items-center justify-between rounded-md border border-rose-200 bg-rose-50/50 p-3" data-testid={`hp-lead-${l.id}`}>
              <div>
                <div className="flex items-center gap-1.5 text-sm font-medium text-slate-900"><Flame className="h-3.5 w-3.5 text-rose-500" />{l.name}</div>
                <div className="text-xs text-slate-500">{l.company || l.source}{l.city ? ` · ${l.city}` : ""}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-slate-900">{inr(l.value)}</div>
                <div className="text-xs capitalize text-slate-500">{stageLabels[l.stage] || STAGE_LABELS[l.stage] || l.stage}</div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      )}
      {showStockAnalytics && showWidget("stock") && (
        <div className="space-y-4" data-testid="admin-stock-analytics">
          <h2 className="font-heading text-2xl font-semibold text-slate-900">Stock Movement Analytics</h2>
          <StockAnalytics />
        </div>
      )}
    </div>
  );
}
